// V5 剧本生成任务详情页 - 独立版本
// v5 使用完全不同的数据结构和模板系统

import React, { useEffect, useState, useCallback, useRef } from 'react'
import {
  Card,
  Descriptions,
  Progress,
  Button,
  Space,
  Tag,
  message,
  Spin,
  Alert,
  Tabs,
  Empty,
  Timeline,
  List,
  Row,
  Col,
  Divider,
  Typography,
} from 'antd'
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  EyeOutlined,
  StopOutlined,
  PauseOutlined,
  PlayCircleOutlined,
  ExclamationCircleOutlined,
  RocketOutlined,
  FileTextOutlined,
  ExperimentOutlined,
  GlobalOutlined,
  TeamOutlined,
  GiftOutlined,
  ClusterOutlined,
  BookOutlined,
  UserOutlined,
  SolutionOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import { useParams, useNavigate } from 'react-router-dom'
import type { TabsProps } from 'antd'
import {
  getScriptGenerationTaskDetail,
  getScriptGenerationTaskStatus,
  cancelScriptGenerationTask,
  pauseScriptGenerationTask,
  resumeScriptGenerationTask,
  retryScriptGenerationTaskFromPhase,
  retryScriptGenerationTask,
  startPendingScriptGenerationTask,
} from '@/services/scriptGeneration'
import type { ScriptGenerationTaskDetailResponse, PhaseResult } from '@/types/scriptGeneration'
import {
  STATUS_COLOR,
  STATUS_LABEL,
  SCRIPT_GENERATION_CONFIG,
  isRunningStatus,
  isTerminalStatus,
  isCancellableStatus,
} from '@/constants/scriptGeneration'
import { handleApiError } from '@/utils/errorHandler'
import AICallLogs from './components/AICallLogs'
import './TaskDetail.css'

const { Text, Title } = Typography

// V5 Template 配置 (11 Templates)
const V5_TEMPLATE_CONFIG = [
  { key: 1, templateId: 'T1', name: '世界圣经', icon: <BookOutlined /> },
  { key: 2, templateId: 'T7', name: '游戏流程', icon: <ExperimentOutlined /> },
  { key: 3, templateId: 'T4', name: '公共事件', icon: <GlobalOutlined /> },
  { key: 4, templateId: 'T5', name: '人物小传', icon: <TeamOutlined /> },
  { key: 5, templateId: 'T6', name: '交集点', icon: <GiftOutlined /> },
  { key: 6, templateId: 'T3', name: '关系网络', icon: <ClusterOutlined /> },
  { key: 7, templateId: 'T2', name: '个人故事', icon: <FileTextOutlined /> },
  { key: 8, templateId: 'T9', name: '演员简报', icon: <SolutionOutlined /> },
  { key: 9, templateId: 'T8', name: '多结局设计', icon: <CheckCircleOutlined /> },
  { key: 10, templateId: 'T10', name: '散场互动', icon: <TeamOutlined /> },
  { key: 11, templateId: 'T11', name: 'DM手册', icon: <BookOutlined /> },
]

// 获取阶段状态
const getPhaseStatus = (phaseResults: PhaseResult[], phaseKey: number): 'completed' | 'running' | 'failed' | 'pending' => {
  const log = phaseResults.find((l) => l.phase === phaseKey)
  if (!log) return 'pending'
  if (log.status) {
    return log.status === 'completed' ? 'completed' : log.status === 'failed' ? 'failed' : 'running'
  }
  return log.success ? 'completed' : 'failed'
}

/**
 * T1: 世界圣经展示
 */
const T1WorldBibleViewer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const era = data.era
  const worldSetting = data.world_setting
  const coreTruth = data.core_truth
  const spaces = data.spaces as Array<{
    id: string
    name: unknown
    type: unknown
    description: unknown
  }> || []
  const worldNpcs = data.world_npcs as Array<{
    id: string
    name: unknown
    role: unknown
    description: unknown
  }> || []

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Card size="small" title="世界设定">
        <Descriptions column={1} size="small">
          <Descriptions.Item label="时代">
            {typeof era === 'string' ? era : era ? JSON.stringify(era) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="世界设定">
            {typeof worldSetting === 'string' ? worldSetting : worldSetting ? JSON.stringify(worldSetting) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="核心真相">
            {typeof coreTruth === 'string' ? coreTruth : coreTruth ? JSON.stringify(coreTruth) : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {spaces.length > 0 && (
        <Card size="small" title={`空间列表 (${spaces.length})`}>
          <Row gutter={[16, 16]}>
            {spaces.map((space) => (
              <Col span={12} key={space.id}>
                <Card size="small" type="inner">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="名称">
                      <Tag color={space.type === 'public' ? 'blue' : 'orange'}>
                        {space.type === 'public' ? '公共' : '私密'}
                      </Tag>
                      {typeof space.name === 'string' ? space.name : JSON.stringify(space.name)}
                    </Descriptions.Item>
                    <Descriptions.Item label="描述">
                      {typeof space.description === 'string' ? space.description : JSON.stringify(space.description)}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {worldNpcs.length > 0 && (
        <Card size="small" title={`世界NPC (${worldNpcs.length})`}>
          <Row gutter={[16, 16]}>
            {worldNpcs.map((npc) => (
              <Col span={12} key={npc.id}>
                <Card size="small" type="inner" title={typeof npc.name === 'string' ? npc.name : JSON.stringify(npc.name)}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="角色">
                      {typeof npc.role === 'string' ? npc.role : JSON.stringify(npc.role)}
                    </Descriptions.Item>
                    <Descriptions.Item label="描述">
                      {typeof npc.description === 'string' ? npc.description : JSON.stringify(npc.description)}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}
    </Space>
  )
}

/**
 * T7: 游戏流程展示
 */
const T7GameFlowViewer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const phases = data.phases as Array<{
    id: string
    name: string
    type: string
    duration_minutes: number
    objective: string
  }> || []
  const timeline = data.timeline as Array<{ time: string; event: string }> || []

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {phases.length > 0 && (
        <Card size="small" title={`游戏阶段 (${phases.length})`}>
          <Timeline
            mode="left"
            items={phases.map((phase) => ({
              color: 'blue',
              label: `${phase.duration_minutes}分钟`,
              children: (
                <>
                  <Tag color="blue">{phase.type}</Tag>
                  <Text strong>
                    {typeof phase.name === 'string' ? phase.name : JSON.stringify(phase.name)}
                  </Text>
                  <br />
                  <Text type="secondary">
                    {typeof phase.objective === 'string' ? phase.objective : JSON.stringify(phase.objective)}
                  </Text>
                </>
              ),
            }))}
          />
        </Card>
      )}

      {timeline.length > 0 && (
        <Card size="small" title={`时间线 (${timeline.length})`}>
          <Timeline
            mode="left"
            items={timeline.map((item, idx) => ({
              color: 'green',
              label: item.time,
              children: (
                <Text>
                  {typeof item.event === 'string' ? item.event : JSON.stringify(item.event)}
                </Text>
              ),
            }))}
          />
        </Card>
      )}
    </Space>
  )
}

/**
 * T4: 公共事件展示
 */
const T4PublicEventsViewer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const events = data.events as Array<{
    id: string
    name: string
    phase: string
    description: string
    purpose: string
  }> || []
  const mainPlot = data.main_plot

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {mainPlot && (
        <Card size="small" title="主线剧情">
          <Text>
            {typeof mainPlot === 'string'
              ? mainPlot
              : JSON.stringify(mainPlot)}
          </Text>
        </Card>
      )}

      {events.length > 0 && (
        <Card size="small" title={`公共事件 (${events.length})`}>
          <Row gutter={[16, 16]}>
            {events.map((evt) => (
              <Col span={12} key={evt.id}>
                <Card size="small" type="inner" title={typeof evt.name === 'string' ? evt.name : JSON.stringify(evt.name)}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="阶段">
                      <Tag color="blue">{typeof evt.phase === 'string' ? evt.phase : JSON.stringify(evt.phase)}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="描述">
                      {typeof evt.description === 'string' ? evt.description : JSON.stringify(evt.description)}
                    </Descriptions.Item>
                    <Descriptions.Item label="目的">
                      {typeof evt.purpose === 'string' ? evt.purpose : JSON.stringify(evt.purpose)}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}
    </Space>
  )
}

/**
 * T5: 人物小传展示
 */
const T5CharacterBiosViewer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const playerBiographies = data.player_biographies as Array<{
    name: string
    basic_info?: { age?: number; gender?: string; occupation?: string }
    background?: string
    personality?: string
  }> || []
  const actorBiographies = data.actor_biographies as Array<{
    name: string
    basic_info?: { age?: number; gender?: string; occupation?: string }
    background?: string
  }> || []

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {playerBiographies.length > 0 && (
        <Card size="small" title={`玩家角色 (${playerBiographies.length})`}>
          <Row gutter={[16, 16]}>
            {playerBiographies.map((player, idx) => (
              <Col span={12} key={idx}>
                <Card size="small" type="inner" title={typeof player.name === 'string' ? player.name : JSON.stringify(player.name)}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="基本信息">
                      {player.basic_info?.gender || '-'} · {player.basic_info?.age || '-'}岁 · {player.basic_info?.occupation || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="背景">
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {typeof player.background === 'string'
                          ? `${player.background.substring(0, 200)}...`
                          : player.background
                            ? JSON.stringify(player.background).substring(0, 200)
                            : '-'}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="性格">
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {typeof player.personality === 'string'
                          ? `${player.personality.substring(0, 200)}...`
                          : player.personality
                            ? JSON.stringify(player.personality).substring(0, 200)
                            : '-'}
                      </Text>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {actorBiographies.length > 0 && (
        <Card size="small" title={`演员角色 (${actorBiographies.length})`}>
          <Row gutter={[16, 16]}>
            {actorBiographies.map((actor, idx) => (
              <Col span={12} key={idx}>
                <Card size="small" type="inner" title={typeof actor.name === 'string' ? actor.name : JSON.stringify(actor.name)}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="基本信息">
                      {actor.basic_info?.gender || '-'} · {actor.basic_info?.age || '-'}岁 · {actor.basic_info?.occupation || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="背景">
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {typeof actor.background === 'string'
                          ? `${actor.background.substring(0, 200)}...`
                          : actor.background
                            ? JSON.stringify(actor.background).substring(0, 200)
                            : '-'}
                      </Text>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}
    </Space>
  )
}

/**
 * T6: 交集点展示
 */
const T6IntersectionPointsViewer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const intersectionPoints = data.intersection_points as Array<{
    id: string
    name: string
    type: string
    timing: string
    location: string
    content: string
    purpose: string
  }> || []

  return (
    <Card size="small" title={`交集点场景 (${intersectionPoints.length})`}>
      <List
        dataSource={intersectionPoints}
        renderItem={(point) => (
          <List.Item>
            <Card size="small" type="inner" style={{ width: '100%' }} title={typeof point.name === 'string' ? point.name : JSON.stringify(point.name)}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="类型">
                  <Tag color="blue">{typeof point.type === 'string' ? point.type : JSON.stringify(point.type)}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="时间">
                  {typeof point.timing === 'string' ? point.timing : JSON.stringify(point.timing)}
                </Descriptions.Item>
                <Descriptions.Item label="地点">
                  {typeof point.location === 'string' ? point.location : JSON.stringify(point.location)}
                </Descriptions.Item>
                <Descriptions.Item label="内容">
                  <Text type="secondary">
                    {typeof point.content === 'string' ? point.content : JSON.stringify(point.content)}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="目的">
                  {typeof point.purpose === 'string' ? point.purpose : JSON.stringify(point.purpose)}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </List.Item>
        )}
      />
    </Card>
  )
}

/**
 * T3: 关系网络展示
 */
const T3RelationsViewer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const relations = data.relations as Array<{
    from: string
    to: string
    type: string
    description: string
  }> || []
  const relationGraph = data.relation_graph as Record<string, unknown>

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {relations.length > 0 && (
        <Card size="small" title={`关系定义 (${relations.length})`}>
          <List
            dataSource={relations}
            renderItem={(rel) => (
              <List.Item>
                <Space>
                  <Tag color="blue">{typeof rel.from === 'string' ? rel.from : JSON.stringify(rel.from)}</Tag>
                  <Text>→</Text>
                  <Tag color="green">{typeof rel.to === 'string' ? rel.to : JSON.stringify(rel.to)}</Tag>
                  <Tag color="purple">{typeof rel.type === 'string' ? rel.type : JSON.stringify(rel.type)}</Tag>
                </Space>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {typeof rel.description === 'string' ? rel.description : JSON.stringify(rel.description)}
                </Text>
              </List.Item>
            )}
          />
        </Card>
      )}
    </Space>
  )
}

/**
 * T2: 个人故事展示
 */
const T2PersonalStoriesViewer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  // 尝试多种可能的字段名
  const playerStories = (data.player_stories || data.playerStories || []) as Array<Record<string, unknown>>
  const actorStories = (data.actor_stories || data.actorStories || []) as Array<Record<string, unknown>>

  // 提取故事内容的辅助函数
  const getStoryContent = (item: Record<string, unknown>): string => {
    // 尝试多种可能的字段名
    const possibleFields = ['story', 'content', 'personal_story', 'background', 'narrative', 'plot']
    for (const field of possibleFields) {
      const value = item[field]
      if (typeof value === 'string' && value.length > 0) {
        return value
      }
      if (value && typeof value === 'object') {
        return JSON.stringify(value)
      }
    }
    // 如果没有找到，返回整个对象的 JSON
    return JSON.stringify(item)
  }

  // 获取名称的辅助函数
  const getName = (item: Record<string, unknown>, idField: string, nameField: string): string => {
    return (item[nameField] as string) || (item[idField] as string) || '未知'
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {playerStories.length > 0 && (
        <Card size="small" title={`玩家个人故事 (${playerStories.length})`}>
          <List
            dataSource={playerStories}
            renderItem={(item, index) => (
              <List.Item>
                <Card
                  size="small"
                  type="inner"
                  style={{ width: '100%' }}
                  title={getName(item, 'player_id', 'player_name')}
                >
                  <Text type="secondary">
                    {getStoryContent(item).substring(0, 500)}
                    {getStoryContent(item).length > 500 ? '...' : ''}
                  </Text>
                  {/* 调试信息 */}
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ fontSize: 12, color: '#999', cursor: 'pointer' }}>查看原始数据</summary>
                    <pre style={{ fontSize: 11, background: '#f5f5f5', padding: 8, marginTop: 4 }}>
                      {JSON.stringify(item, null, 2)}
                    </pre>
                  </details>
                </Card>
              </List.Item>
            )}
          />
        </Card>
      )}

      {actorStories.length > 0 && (
        <Card size="small" title={`演员个人故事 (${actorStories.length})`}>
          <List
            dataSource={actorStories}
            renderItem={(item, index) => (
              <List.Item>
                <Card
                  size="small"
                  type="inner"
                  style={{ width: '100%' }}
                  title={getName(item, 'actor_id', 'actor_name')}
                >
                  <Text type="secondary">
                    {getStoryContent(item).substring(0, 500)}
                    {getStoryContent(item).length > 500 ? '...' : ''}
                  </Text>
                  {/* 调试信息 */}
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ fontSize: 12, color: '#999', cursor: 'pointer' }}>查看原始数据</summary>
                    <pre style={{ fontSize: 11, background: '#f5f5f5', padding: 8, marginTop: 4 }}>
                      {JSON.stringify(item, null, 2)}
                    </pre>
                  </details>
                </Card>
              </List.Item>
            )}
          />
        </Card>
      )}
    </Space>
  )
}

/**
 * T9/T8/T10/T11: 最终阶段展示
 */
const T9FinalViewer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const actorBriefings = data.actor_briefings as Array<{
    actor_id: string
    actor_name: string
    briefing: string
  }> || []
  const groupGuides = data.group_guides as Array<{
    group_id: string
    guide: string
  }> || []

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {actorBriefings.length > 0 && (
        <Card size="small" title={`演员简报 (${actorBriefings.length})`}>
          <List
            dataSource={actorBriefings}
            renderItem={(item) => (
              <List.Item>
                <Card size="small" type="inner" style={{ width: '100%' }} title={item.actor_name}>
                  <Text type="secondary">
                    {typeof item.briefing === 'string'
                      ? `${item.briefing.substring(0, 500)}...`
                      : item.briefing
                        ? JSON.stringify(item.briefing).substring(0, 500)
                        : '-'}
                  </Text>
                </Card>
              </List.Item>
            )}
          />
        </Card>
      )}

      {groupGuides.length > 0 && (
        <Card size="small" title={`分组指南 (${groupGuides.length})`}>
          <List
            dataSource={groupGuides}
            renderItem={(item) => (
              <List.Item>
                <Card size="small" type="inner" style={{ width: '100%' }} title={item.group_id}>
                  <Text type="secondary">
                    {typeof item.guide === 'string'
                      ? `${item.guide.substring(0, 500)}...`
                      : item.guide
                        ? JSON.stringify(item.guide).substring(0, 500)
                        : '-'}
                  </Text>
                </Card>
              </List.Item>
            )}
          />
        </Card>
      )}
    </Space>
  )
}

/**
 * 阶段产物查看器 - 根据阶段渲染对应组件
 */
const PhaseOutputV5: React.FC<{ phase: number; data: Record<string, unknown> }> = ({ phase, data }) => {
  if (!data || Object.keys(data).length === 0) {
    return <Alert message="暂无产物数据" type="warning" showIcon />
  }

  switch (phase) {
    case 1: return <T1WorldBibleViewer data={data} />
    case 2: return <T7GameFlowViewer data={data} />
    case 3: return <T4PublicEventsViewer data={data} />
    case 4: return <T5CharacterBiosViewer data={data} />
    case 5: return <T6IntersectionPointsViewer data={data} />
    case 6: return <T3RelationsViewer data={data} />
    case 7: return <T2PersonalStoriesViewer data={data} />
    case 8: return <T9FinalViewer data={data} />
    case 9: return <T9FinalViewer data={data} />  // T8 多结局 - 复用最终查看器
    case 10: return <T9FinalViewer data={data} /> // T10 散场互动 - 复用最终查看器
    case 11: return <T9FinalViewer data={data} /> // T11 DM手册 - 复用最终查看器
    default: return <Alert message={`未知阶段 ${phase}`} type="error" />
  }
}

/**
 * V5 任务详情页
 */
const ScriptGenerationTaskDetailV5: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>()
  const navigate = useNavigate()
  const [messageApi, contextHolder] = message.useMessage()

  // 状态管理
  const [taskDetail, setTaskDetail] = useState<ScriptGenerationTaskDetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [polling, setPolling] = useState(false)

  // Refs
  const isMountedRef = useRef(true)
  const pollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const prevStatusRef = useRef<string | undefined>(undefined)

  // 加载任务详情
  const loadTaskDetail = useCallback(async () => {
    if (!taskId || !isMountedRef.current) return null
    setLoading(true)
    try {
      const detail = await getScriptGenerationTaskDetail(taskId)
      if (isMountedRef.current) {
        setTaskDetail(detail)
      }
      return detail
    } catch (error) {
      if (isMountedRef.current) {
        handleApiError(error, '加载任务详情失败', messageApi)
      }
      return null
    } finally {
      if (isMountedRef.current) {
        setLoading(false)
      }
    }
  }, [taskId, messageApi])

  // 状态轮询
  const loadTaskStatus = useCallback(async () => {
    if (!taskId || !isMountedRef.current) return null
    abortControllerRef.current = new AbortController()
    try {
      const status = await getScriptGenerationTaskStatus(taskId)
      if (!isMountedRef.current) return null

      const prevStatus = prevStatusRef.current
      prevStatusRef.current = status.status

      setTaskDetail((prev) => {
        if (!prev) return prev
        if (status.status && isTerminalStatus(status.status as any)) {
          setTimeout(() => loadTaskDetail(), 0)
        }
        return {
          ...prev,
          status: status.status as any,
          progress: status.progress,
          currentPhase: status.current_phase,
          phaseName: status.phase_name,
          errorMessage: status.error_message,
          phaseResults: (status.phase_results || prev.phaseResults) as any,
        }
      })
      return status
    } catch (error) {
      return null
    } finally {
      abortControllerRef.current = null
    }
  }, [taskId, loadTaskDetail])

  // 停止轮询
  const stopPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current)
      pollingTimerRef.current = null
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setPolling(false)
  }, [])

  // 启动轮询
  const startPolling = useCallback(() => {
    stopPolling()
    setPolling(true)
    let pollCount = 0
    const baseInterval = SCRIPT_GENERATION_CONFIG.POLL_INTERVAL
    const maxInterval = 10000

    const poll = async (currentInterval: number) => {
      if (!isMountedRef.current) {
        stopPolling()
        return
      }
      try {
        const status = await loadTaskStatus()
        if (!isMountedRef.current) {
          stopPolling()
          return
        }
        if (status && isTerminalStatus(status.status as any)) {
          stopPolling()
          return
        }
        pollCount++
        const nextInterval = Math.min(baseInterval * Math.pow(1.5, pollCount), maxInterval)
        pollingTimerRef.current = setTimeout(() => poll(nextInterval), currentInterval)
      } catch (error) {
        if (isMountedRef.current) {
          pollingTimerRef.current = setTimeout(() => poll(baseInterval), baseInterval)
        }
      }
    }
    pollingTimerRef.current = setTimeout(() => poll(baseInterval), baseInterval)
  }, [loadTaskStatus, stopPolling])

  // 初始化
  useEffect(() => {
    const init = async () => {
      const detail = await loadTaskDetail()
      if (detail && isMountedRef.current && detail.status) {
        if (isRunningStatus(detail.status as any)) {
          startPolling()
        }
        prevStatusRef.current = detail.status
      }
    }
    init()
    return () => {
      isMountedRef.current = false
      stopPolling()
    }
  }, [loadTaskDetail, startPolling, stopPolling])

  // 操作处理
  const handleBack = useCallback(() => navigate('/script-generation'), [navigate])
  const handlePreview = useCallback(() => {
    if (taskId) navigate(`/script-generation/${taskId}/preview`)
  }, [taskId, navigate])

  const handleCancel = useCallback(async () => {
    if (!taskId) return
    try {
      await cancelScriptGenerationTask(taskId)
      messageApi.success('任务已取消')
      stopPolling()
      await loadTaskDetail()
    } catch (error) {
      handleApiError(error, '取消任务失败', messageApi)
    }
  }, [taskId, messageApi, stopPolling, loadTaskDetail])

  const handleRetryAll = useCallback(async () => {
    if (!taskId) return
    try {
      await retryScriptGenerationTask(taskId)
      messageApi.success('任务已重试')
      await loadTaskDetail()
      startPolling()
    } catch (error) {
      handleApiError(error, '重试失败', messageApi)
    }
  }, [taskId, messageApi, loadTaskDetail, startPolling])

  const handleRetryPhase = useCallback(async (phase: number) => {
    if (!taskId) return
    try {
      await retryScriptGenerationTaskFromPhase(taskId, phase)
      messageApi.success(`已从阶段 ${phase} 重试`)
      await loadTaskDetail()
      startPolling()
    } catch (error) {
      handleApiError(error, '重试阶段失败', messageApi)
    }
  }, [taskId, messageApi, loadTaskDetail, startPolling])

  // 早期返回
  if (!taskId) return null
  if (loading) return <div style={{ padding: 48, textAlign: 'center' }}><Spin size="large" /></div>
  if (!taskDetail) return <div style={{ padding: 48, textAlign: 'center' }}><Empty description="任务不存在" /></div>

  const isTaskCompleted = taskDetail.status === 'completed'
  const isTaskFailed = taskDetail.status === 'failed'
  const isTaskRunning = isRunningStatus(taskDetail.status)

  // 阶段进度 Tab
  const PhaseProgressTab = () => {
    const [selectedPhase, setSelectedPhase] = useState<number | null>(null)

    return (
      <div style={{ display: 'flex', gap: 24 }}>
        {/* 左侧：阶段列表 */}
        <div style={{ width: 320, flexShrink: 0 }}>
          <Card size="small" title="生成阶段">
            <Timeline mode="left">
              {V5_TEMPLATE_CONFIG.map((phase) => {
                const status = getPhaseStatus(taskDetail?.phase_results || [], phase.key)
                const phaseResult = taskDetail?.phase_results?.find((p) => p.phase === phase.key)
                const isSelected = selectedPhase === phase.key

                return (
                  <Timeline.Item
                    key={phase.key}
                    color={
                      status === 'completed' ? 'green' :
                      status === 'running' ? 'blue' :
                      status === 'failed' ? 'red' :
                      'gray'
                    }
                    dot={phase.icon}
                  >
                    <div
                      style={{
                        padding: 8,
                        borderRadius: 4,
                        cursor: 'pointer',
                        background: isSelected ? '#e6f7ff' : 'transparent',
                        border: isSelected ? '1px solid #1890ff' : '1px solid transparent',
                      }}
                      onClick={() => setSelectedPhase(phase.key)}
                    >
                      <Space direction="vertical" size={0} style={{ width: '100%' }}>
                        <Text strong>{phase.name}</Text>
                        <Space>
                          <Tag color={
                            status === 'completed' ? 'success' :
                            status === 'running' ? 'processing' :
                            status === 'failed' ? 'error' :
                            'default'
                          }>
                            {status === 'completed' ? '完成' :
                             status === 'running' ? '进行中' :
                             status === 'failed' ? '失败' :
                             '等待'}
                          </Tag>
                          {phaseResult?.ai_calls && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {phaseResult.ai_calls}次调用
                            </Text>
                          )}
                        </Space>
                        {/* 每个阶段都显示重试按钮 */}
                        <Button
                          size="small"
                          danger={isTaskRunning}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleRetryPhase(phase.key)
                          }}
                        >
                          {isTaskRunning ? '强制重试' : '重试此阶段'}
                        </Button>
                      </Space>
                    </div>
                  </Timeline.Item>
                )
              })}
            </Timeline>
          </Card>
        </div>

        {/* 右侧：阶段详情 */}
        <div style={{ flex: 1 }}>
          {selectedPhase ? (() => {
            const phaseResult = taskDetail?.phase_results?.find((p) => p.phase === selectedPhase)
            const phaseConfig = V5_TEMPLATE_CONFIG.find((p) => p.key === selectedPhase)

            if (!phaseResult) {
              return (
                <Card size="small">
                  <Empty description={`${phaseConfig?.name || `阶段 ${selectedPhase}`} 尚未执行`} />
                </Card>
              )
            }

            return (
              <Card
                size="small"
                title={
                  <Space>
                    {phaseConfig?.icon}
                    <span>{phaseConfig?.name || `阶段 ${phaseResult.phase}`}</span>
                    <Tag color={phaseResult.status === 'completed' ? 'success' : 'error'}>
                      {phaseResult.status === 'completed' ? '完成' : '失败'}
                    </Tag>
                  </Space>
                }
                extra={
                  <Space>
                    <Text type="secondary">{phaseResult.duration_ms}ms</Text>
                    <Tag color="cyan">{phaseResult.tokens} tokens</Tag>
                    <Button
                      size="small"
                      danger={isTaskRunning}
                      onClick={() => handleRetryPhase(phaseResult.phase)}
                    >
                      {isTaskRunning ? '强制重试' : '重试此阶段'}
                    </Button>
                  </Space>
                }
              >
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={8}>
                    <Text type="secondary">AI 调用</Text>
                    <div><Text strong>{phaseResult.ai_calls} 次</Text></div>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary">Token</Text>
                    <div><Text strong>{phaseResult.tokens}</Text></div>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary">成本</Text>
                    <div><Text strong>¥{phaseResult.cost_yuan?.toFixed(4)}</Text></div>
                  </Col>
                </Row>

                {phaseResult.data && (
                  <PhaseOutputV5 phase={phaseResult.phase} data={phaseResult.data as Record<string, unknown>} />
                )}
              </Card>
            )
          })() : (
            <Card size="small">
              <Empty description="点击左侧阶段查看详情" />
            </Card>
          )}
        </div>
      </div>
    )
  }

  // Tab 配置
  const tabItems: TabsProps['items'] = [
    {
      key: 'overview',
      label: '概览',
      children: <PhaseProgressTab />,
    },
    {
      key: 'logs',
      label: '阶段产物',
      children: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {taskDetail?.phase_results?.length ? (
            taskDetail.phase_results.map((phaseResult) => (
              <Card
                key={phaseResult.phase}
                size="small"
                title={
                  <Space>
                    {V5_TEMPLATE_CONFIG.find((p) => p.key === phaseResult.phase)?.icon}
                    <span>
                      {V5_TEMPLATE_CONFIG.find((p) => p.key === phaseResult.phase)?.name || `阶段 ${phaseResult.phase}`}
                    </span>
                  </Space>
                }
              >
                {phaseResult.data ? (
                  <PhaseOutputV5 phase={phaseResult.phase} data={phaseResult.data as Record<string, unknown>} />
                ) : (
                  <Empty description="暂无产物数据" />
                )}
              </Card>
            ))
          ) : (
            <Empty description="暂无阶段产物数据" />
          )}
        </div>
      ),
    },
    {
      key: 'ai_calls',
      label: (
        <span>
          AI 调用记录
          {taskDetail?.ai_call_logs && taskDetail.ai_call_logs.length > 0 && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {taskDetail.ai_call_logs.length}
            </Tag>
          )}
        </span>
      ),
      children: taskDetail ? (
        <AICallLogs
          logs={taskDetail.ai_call_logs || []}
          loading={loading}
        />
      ) : <Spin />,
    },
  ]

  return (
    <div className="script-generation-task-detail">
      {contextHolder}
      <Card
        title={
          <Space>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack}>
              返回
            </Button>
            <span>{taskDetail.task_name}</span>
            <Tag color="default">#{taskId}</Tag>
            <Tag color={STATUS_COLOR[taskDetail.status]}>{STATUS_LABEL[taskDetail.status]}</Tag>
            <Tag color="purple">v5.0</Tag>
            {polling && (
              <Tag color="processing" icon={<Spin size="small" />}>
                实时更新中
              </Tag>
            )}
          </Space>
        }
        extra={
          <Space>
            {isTaskRunning && (
              <Button danger icon={<StopOutlined />} onClick={handleCancel}>
                取消
              </Button>
            )}
            {isTaskFailed && (
              <Button type="primary" icon={<ReloadOutlined />} onClick={handleRetryAll}>
                重试
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={loadTaskDetail}>
              刷新
            </Button>
            {isTaskCompleted && (
              <Button type="primary" icon={<EyeOutlined />} onClick={handlePreview}>
                查看配置
              </Button>
            )}
          </Space>
        }
      >
        {/* 进度条 */}
        <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>生成进度</span>
            <span style={{ color: '#666' }}>{taskDetail.progress}%</span>
          </div>
          <Progress
            percent={taskDetail.progress}
            status={isTaskCompleted ? 'success' : isTaskFailed ? 'exception' : 'active'}
            size="small"
          />
          {taskDetail.current_phase && (
            <div style={{ color: '#666', fontSize: 12, marginTop: 8 }}>
              当前阶段: {V5_TEMPLATE_CONFIG.find((p) => p.key === taskDetail.current_phase)?.name || taskDetail.current_phase}
            </div>
          )}

          {isTaskFailed && taskDetail.error_message && (
            <Alert
              message="执行出错"
              description={taskDetail.error_message}
              type="error"
              showIcon
              style={{ marginTop: 12 }}
            />
          )}
        </div>

        <Tabs items={tabItems} />
      </Card>
    </div>
  )
}

export default ScriptGenerationTaskDetailV5
